import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowRight, CheckCircle } from 'lucide-react'
import axios from 'axios'
import Navbar from '@/components/layout/Navbar'
import Footer from '@/components/layout/Footer'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL
const API = `${BACKEND_URL}/api`

const SERVICE_OPTIONS = [
  'Tech Leadership Search',
  'GCC Talent Setup & Scaling',
  'Data Science & AI Leadership',
  'Product Leadership',
  'Technology Architecture',
  'Founding Team Recruitment',
  'Other / Custom Brief',
]

const TIMELINE_OPTIONS = [
  'Within 30 days',
  '1–3 months',
  '3–6 months',
  '6–12 months',
  'Exploring / No set timeline',
]

const inputClass =
  'w-full bg-transparent border-b border-[#E5E7EB] focus:border-[#0B1F3A] outline-none py-3 text-sm font-body text-[#0A0A0A] placeholder-[#0A0A0A]/30 transition-colors duration-200'

const labelClass = 'block text-[9px] tracking-[0.28em] uppercase font-body font-semibold text-[#0A0A0A]/40 mb-2'

const lineV = {
  hidden: { y: '110%' },
  visible: (i) => ({
    y: '0%',
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: i * 0.1 },
  }),
}

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false)
  const [serverError, setServerError] = useState('')

  // Pre-fill service & timeline from URL params (?service=...&timeline=...)
  const params = new URLSearchParams(window.location.search)
  const defaultService = params.get('service') || ''
  const defaultTimeline = params.get('timeline') || ''

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      service: defaultService,
      expected_timeline: defaultTimeline,
    },
  })

  const onSubmit = async (data) => {
    setServerError('')
    try {
      const payload = {
        ...data,
        honeypot: data.honeypot || '',
        source_page: window.location.pathname,
        utm_source: new URLSearchParams(window.location.search).get('utm_source') || undefined,
        utm_medium: new URLSearchParams(window.location.search).get('utm_medium') || undefined,
        utm_campaign: new URLSearchParams(window.location.search).get('utm_campaign') || undefined,
      }
      await axios.post(`${API}/contact`, payload)
      setSubmitted(true)
    } catch (err) {
      if (err.response?.status === 429) {
        setServerError('You have already submitted recently. Please wait a few minutes.')
      } else {
        setServerError('Something went wrong. Please try again or email sahil@strivepartners.co')
      }
    }
  }

  return (
    <main data-testid="contact-page">
      <Navbar />

      <section className="pt-40 pb-32 px-6 md:px-12 lg:px-24 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-28">
            {/* Left: heading */}
            <div>
              <motion.div initial="hidden" animate="visible">
                <div className="overflow-hidden mb-8">
                  <motion.p custom={0} variants={lineV} className="text-[10px] tracking-[0.38em] uppercase font-body font-semibold text-[#16365F]">
                    Start a Search
                  </motion.p>
                </div>
                <div className="overflow-hidden mb-8">
                  <motion.h1 custom={1} variants={lineV}
                    className="text-[clamp(2.5rem,6vw,6rem)] font-heading font-black tracking-tighter leading-none text-[#0A0A0A]"
                    data-testid="contact-heading"
                  >
                    Tell us<br />about your<br />
                    <span className="font-light text-[#16365F]">mandate.</span>
                  </motion.h1>
                </div>
                <div className="overflow-hidden">
                  <motion.p custom={2} variants={lineV} className="text-base font-body text-[#0A0A0A]/50 leading-relaxed max-w-sm">
                    A Strive Partners search consultant will respond within one business day.
                  </motion.p>
                </div>
              </motion.div>

              <div className="mt-16 space-y-8">
                <div>
                  <p className="text-[9px] tracking-[0.28em] uppercase font-body font-semibold text-[#0A0A0A]/30 mb-1">Email</p>
                  <a href="mailto:sahil@strivepartners.co" className="text-sm font-body text-[#0A0A0A]/60 hover:text-[#0B1F3A] transition-colors">
                    sahil@strivepartners.co
                  </a>
                </div>
                <div>
                  <p className="text-[9px] tracking-[0.28em] uppercase font-body font-semibold text-[#0A0A0A]/30 mb-1">Phone</p>
                  <a href="tel:+918005812130" className="text-sm font-body text-[#0A0A0A]/60 hover:text-[#0B1F3A] transition-colors">
                    +91 80058 12130
                  </a>
                </div>
                <div>
                  <p className="text-[9px] tracking-[0.28em] uppercase font-body font-semibold text-[#0A0A0A]/30 mb-1">Location</p>
                  <p className="text-sm font-body text-[#0A0A0A]/60">Bengaluru, India</p>
                </div>
              </div>
            </div>

            {/* Right: form */}
            <div>
              {submitted ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="flex flex-col items-start gap-6 py-16"
                  data-testid="contact-success"
                >
                  <CheckCircle size={32} className="text-[#0B1F3A]" strokeWidth={1.5} />
                  <h2 className="text-2xl font-heading font-black text-[#0A0A0A] tracking-tight">
                    Thank you.
                  </h2>
                  <p className="text-base font-body text-[#0A0A0A]/60 leading-relaxed max-w-md">
                    A Strive Partners search consultant will respond within one business day.
                  </p>
                  <Link to="/" className="inline-flex items-center gap-2 text-[10px] tracking-[0.18em] uppercase font-body font-semibold text-[#0A0A0A] border-b border-[#0A0A0A]/30 pb-0.5">
                    Back to home <ArrowRight size={11} />
                  </Link>
                </motion.div>
              ) : (
                <motion.form
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                  onSubmit={handleSubmit(onSubmit)}
                  data-testid="contact-form"
                  noValidate
                >
                  {/* Honeypot — hidden */}
                  <input type="text" className="hidden" tabIndex={-1} autoComplete="off" {...register('honeypot')} />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8 mb-8">
                    <div>
                      <label className={labelClass}>Full Name *</label>
                      <input
                        {...register('full_name', { required: 'Required' })}
                        placeholder="Priya Sharma"
                        className={inputClass}
                        data-testid="contact-input-name"
                      />
                      {errors.full_name && <p className="text-xs text-red-500 mt-1 font-body">{errors.full_name.message}</p>}
                    </div>
                    <div>
                      <label className={labelClass}>Work Email *</label>
                      <input
                        {...register('work_email', {
                          required: 'Required',
                          pattern: { value: /^[^@]+@[^@]+\.[^@]+$/, message: 'Enter a valid email' },
                        })}
                        type="email"
                        placeholder="priya@company.com"
                        className={inputClass}
                        data-testid="contact-input-email"
                      />
                      {errors.work_email && <p className="text-xs text-red-500 mt-1 font-body">{errors.work_email.message}</p>}
                    </div>
                    <div>
                      <label className={labelClass}>Company *</label>
                      <input
                        {...register('company', { required: 'Required' })}
                        placeholder="ACME Corporation"
                        className={inputClass}
                        data-testid="contact-input-company"
                      />
                      {errors.company && <p className="text-xs text-red-500 mt-1 font-body">{errors.company.message}</p>}
                    </div>
                    <div>
                      <label className={labelClass}>Hiring Location *</label>
                      <input
                        {...register('hiring_location', { required: 'Required' })}
                        placeholder="Bangalore / Hyderabad"
                        className={inputClass}
                        data-testid="contact-input-location"
                      />
                      {errors.hiring_location && <p className="text-xs text-red-500 mt-1 font-body">{errors.hiring_location.message}</p>}
                    </div>
                  </div>

                  <div className="mb-8">
                    <label className={labelClass}>Current Hiring Requirement *</label>
                    <input
                      {...register('hiring_requirement', { required: 'Required', minLength: { value: 5, message: 'Please provide more detail' } })}
                      placeholder="e.g. VP of Engineering for a new GCC"
                      className={inputClass}
                      data-testid="contact-input-requirement"
                    />
                    {errors.hiring_requirement && <p className="text-xs text-red-500 mt-1 font-body">{errors.hiring_requirement.message}</p>}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8 mb-8">
                    <div>
                      <label className={labelClass}>Service *</label>
                      <select
                        {...register('service', { required: 'Required' })}
                        className={`${inputClass} cursor-pointer`}
                        data-testid="contact-select-service"
                      >
                        <option value="">Select a service</option>
                        {SERVICE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                      {errors.service && <p className="text-xs text-red-500 mt-1 font-body">{errors.service.message}</p>}
                    </div>
                    <div>
                      <label className={labelClass}>Expected Timeline *</label>
                      <select
                        {...register('expected_timeline', { required: 'Required' })}
                        className={`${inputClass} cursor-pointer`}
                        data-testid="contact-select-timeline"
                      >
                        <option value="">Select a timeline</option>
                        {TIMELINE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                      {errors.expected_timeline && <p className="text-xs text-red-500 mt-1 font-body">{errors.expected_timeline.message}</p>}
                    </div>
                  </div>

                  <div className="mb-10">
                    <label className={labelClass}>Message</label>
                    <textarea
                      {...register('message')}
                      rows={4}
                      placeholder="Share any additional context about your search, the role scope, or the challenges you're navigating..."
                      className={`${inputClass} resize-none`}
                      data-testid="contact-input-message"
                    />
                  </div>

                  {serverError && (
                    <p className="text-xs text-red-500 font-body mb-6" data-testid="contact-error">{serverError}</p>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="contact-submit-btn"
                    className="inline-flex items-center gap-3 bg-[#0A0A0A] text-white text-[10px] font-body font-semibold tracking-[0.18em] uppercase px-10 py-4 rounded-none hover:bg-[#0B1F3A] transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Sending...' : <>Submit Brief <ArrowRight size={12} /></>}
                  </button>
                  <p className="text-[9px] font-body text-[#0A0A0A]/25 mt-4 tracking-wide">
                    We do not share your details with third parties.
                  </p>
                </motion.form>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  )
}
